import { createFileRoute, Link } from '@tanstack/react-router'
import { Description, Dialog, DialogPanel, DialogTitle } from '@headlessui/react'
import { contentWrapper } from '../../../styles/contentWrapper';
import { pageTitle, sectionWrapper, titleWrapper } from '../../../styles/sectionStyles';
import ArrowBack from '~icons/material-symbols/arrow-back.svg'
import { css } from '../../../../styled-system/css';
import { NotificationHistoryPreview } from '../../../components/notification-preview';
import { NotificationContentForm } from '../../../components/notification-content-form';
import { LoadingOverlay } from '../../../components/loading-overlay';
import { useCancelNotificationByPersonalInformation, useGetNotificationByPersonalInformationHistories } from '../../../libs/admin/hooks';
import { formatTimestampJaJst } from '../../../libs/utils/format-timestamp';
import { getNotificationSendingTimestamp } from './-get-notification-sending-timestamp';
import type z from 'zod';
import type { buttonTemplateSchema } from '../../../libs/admin/schemas/create-notification';
import type { NotificationByPersonalInformationHistory, PersonalInformationForNotification } from 'schema/gen/es/chiikipoint/model/v2/model_pb';
import { text, textLabel, textWithLabelWrapper } from '../../../styles/textStyles';
import { formatGender } from '../../../libs/utils/format-gender';
import { button } from '../../../styles/buttonStyles';
import { dialogBackdrop } from '../../../styles/dialogStyles';
import { type ReactNode, useState } from 'react';

export const Route = createFileRoute('/admin/notifications/$id')({
  component: RouteComponent,
})

function RouteComponent() {
  return <Layout />;
}

const Layout = () => {
  const { id } = Route.useParams();

  // Fetch all notification histories
  const { data: notificationHistories, isLoading } = useGetNotificationByPersonalInformationHistories();

  // Find the specific notification by id
  const notification = notificationHistories?.find(n => n.id === id);

  // Parse buttonTemplate if it's a string
  const buttonTemplate = notification?.original?.buttonTemplate
    ? JSON.parse(notification.original.buttonTemplate)
    : null;

  if (isLoading) {
    return (
      <div className={contentWrapper()}>
        <LoadingOverlay />
      </div>
    );
  }

  if (!notification) {
    return (
      <div className={contentWrapper()}>
        <Title />
        <div className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}>
          <BreadCrumbs />
          <div className={css({
            padding: '32px',
            textAlign: 'center',
            color: 'text.secondary',
          })}>
            通知が見つかりません
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={contentWrapper()}>
      <Title />
      <div className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}>
        <BreadCrumbs />
        <div className={css({
          display: 'flex',
          gap: '32px',
        })}>
          <NotificationHistoryPreview
            original={notification.original}
            translations={notification.translations}
          >
            {(languageSelector) => (
              <Content
                notification={notification}
                buttonTemplate={buttonTemplate}
                languageSelector={languageSelector}
              />
            )}
          </NotificationHistoryPreview>
        </div>
      </div>
    </div>
  )
}

const Title = () => {
  return (
    <div className={titleWrapper()}>
      <h1 className={pageTitle()}>
        <Link to='/admin/notifications'>
          <ArrowBack />
        </Link>
        プッシュ通知詳細
      </h1>
    </div>
  );
}

const BreadCrumbs = () => {
  return (
    <div className={css({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    })}>
      <Link to="/admin/notifications">
        <span className={css({
          color: 'text.accentPrimary',
          fontWeight: '400',
        })}>
          プッシュ通知
        </span>
      </Link>
      <span className={css({
        color: 'text.tertiary',
      })}>/</span>
      <span className={css({
        color: 'text.tertiary',
      })}>プッシュ通知詳細</span>
    </div>
  );
};

type ButtonTemplate = z.infer<typeof buttonTemplateSchema>;

const formatAddresses = (personalInformation: PersonalInformationForNotification | undefined): string => {
  if (!personalInformation) return "--";
  const { addresses } = personalInformation;
  if (!addresses || addresses.length === 0) return "--";
  return addresses.join(", ");
};

const formatAgeRange = (personalInformation: PersonalInformationForNotification | undefined): string => {
  if (!personalInformation) return "--";
  const { minAge, maxAge } = personalInformation;
  if (minAge === undefined && maxAge === undefined) {
    return "--";
  }
  return `${minAge !== undefined ? `${minAge}歳 ` : ''}〜${maxAge !== undefined ? ` ${maxAge}歳` : ''}`;
}

const formatGenders = (personalInformation: PersonalInformationForNotification | undefined): string => {
  if (!personalInformation) return "--";
  const { genders } = personalInformation;
  if (!genders || genders.length === 0) {
    return "--";
  }
  // lookup gender enums
  return genders.map(formatGender).join(", ");
};

const isFutureScheduled = (timestamp: { seconds: bigint, nanos: number } | undefined) => {
  if (!timestamp) return false;
  const scheduledMillis = Number(timestamp.seconds) * 1000 + Math.floor(timestamp.nanos / 1e6);
  return scheduledMillis > Date.now();
};

const Content = ({
  notification,
  buttonTemplate,
  languageSelector,
}: {
  notification: NotificationByPersonalInformationHistory;
  buttonTemplate: ButtonTemplate | null;
  languageSelector: ReactNode;
}) => {
  const sendingTimestamp = getNotificationSendingTimestamp(notification);
  const navigate = Route.useNavigate();
  const cancelNotification = useCancelNotificationByPersonalInformation()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [cancellingId, setCancellingId] = useState<string | null>(null)
  const canCancel = isFutureScheduled(notification.scheduledAt)

  const handleCancelScheduled = async () => {
    if (!notification.scheduledAt) return
    try {
      setCancellingId(notification.id)
      await cancelNotification.mutateAsync({
        notificationByPersonalInformationHistoryId: notification.id
      })
      navigate({ to: '/admin/notifications' })
    } catch (error) {
      console.error('Failed to cancel scheduled notification', error)
    } finally {
      setCancellingId(null)
      setIsCancelDialogOpen(false)
    }
  }

  const handleOpenCancelDialog = () => {
    if (!canCancel) return
    setIsCancelDialogOpen(true)
  }

  const handleCloseCancelDialog = () => {
    if (cancellingId) return
    setIsCancelDialogOpen(false)
  }

  const isCancelling = Boolean(cancellingId === notification.id)

  const renderCancelDialog = () => {
    if (!isCancelDialogOpen) return null

    return (
      <Dialog
        open
        onClose={handleCloseCancelDialog}
        className={dialogBackdrop()}
      >
        <DialogPanel className={css({
          backgroundColor: 'white',
          width: '320px',
          margin: 'auto',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        })}>
          <DialogTitle className={css({
            fontSize: '16px',
            fontWeight: '600',
            textAlign: 'flex-start',
          })}>
            送信予約を取消しますか？
          </DialogTitle>
          <div className={css({
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
          })}>
            <button
              type='button'
              className={button({ color: 'ghost' })}
              onClick={handleCloseCancelDialog}
              disabled={isCancelling}
            >
              キャンセル
            </button>
            <button
              type='button'
              className={button()}
              onClick={handleCancelScheduled}
              disabled={isCancelling}
            >
              送信予約取消
              {isCancelling && <LoadingOverlay size='sm' />}
            </button>
          </div>
        </DialogPanel>
      </Dialog>
    )
  }

  return (
    <div className={css({
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      width: '100%',
    })}>
      <div className={css(
        sectionWrapper.raw(),
        {
          backgroundColor: 'transparent',
        }
      )}>
        <div className={textWithLabelWrapper()}>
          <h2 className={textLabel()}>対象者住所</h2>
          <p className={text()}>{formatAddresses(notification.personalInformation)}</p>
        </div>
        <div className={textWithLabelWrapper()}>
          <h2 className={textLabel()}>年齢範囲</h2>
          <p className={text()}>{formatAgeRange(notification.personalInformation)}</p>
        </div>
        <div className={textWithLabelWrapper()}>
          <h2 className={textLabel()}>対象性別</h2>
          <p className={text()}>{formatGenders(notification.personalInformation)}</p>
        </div>
        <div className={textWithLabelWrapper()}>
          <h2 className={textLabel()}>作成日時</h2>
          <p className={text()}>{notification.createdAt ? formatTimestampJaJst(notification.createdAt, { withTime: true }) : "--"}</p>
        </div>
        <div className={textWithLabelWrapper()}>
          <h2 className={textLabel()}>送信日時</h2>
          <p className={text()}>{sendingTimestamp ? formatTimestampJaJst(sendingTimestamp, { withTime: true }) : "--"}</p>
        </div>
      </div>
      <div className={sectionWrapper()}>
        {/* 通知内容 - All fields are disabled */}
        <NotificationContentForm
          titleTemplateValue={notification.original?.titleTemplate ?? ''}
          onTitleTemplateChange={() => { }} // No-op since fields are disabled
          isTitleTemplateValid={true}
          titleTemplateValidationMessage={undefined}

          messageTemplateValue={notification.original?.messageTemplate ?? ''}
          onMessageTemplateChange={() => { }} // No-op since fields are disabled
          isMessageTemplateValid={true}
          messageTemplateValidationMessage={undefined}

          buttonVariantValue={buttonTemplate?.variant ?? 'none'}
          onButtonVariantChange={() => { }} // No-op since fields are disabled
          isButtonVariantValid={true}

          buttonTextValue={buttonTemplate?.text ?? ''}
          onButtonTextChange={() => { }} // No-op since fields are disabled
          isButtonTextValid={true}
          buttonTextValidationMessage={undefined}

          buttonServiceIdValue={buttonTemplate?.serviceId ?? ''}
          onButtonServiceIdChange={() => { }} // No-op since fields are disabled

          buttonHrefValue={buttonTemplate?.href ?? ''}
          onButtonHrefChange={() => { }} // No-op since fields are disabled
          isButtonHrefValid={true}
          buttonHrefValidationMessage={undefined}

          serviceUrlTemplateValue={notification.original?.serviceUrlTemplate ?? ''}
          onServiceUrlTemplateChange={() => { }} // No-op since fields are disabled
          isServiceUrlTemplateValid={true}
          serviceUrlTemplateValidationMessage={undefined}

          disabled={true} // All fields are disabled
        />
      </div>
      {languageSelector}
      {renderCancelDialog()}
      {canCancel && (
        <div className={css({
          display: 'flex',
        })}>
          <button
            type='button'
            className={button()}
            onClick={handleOpenCancelDialog}
            disabled={isCancelling}
          >
            送信予約取消
          </button>
        </div>
      )}
    </div>
  )
}
